// Types
import type { TAutomatedEmailFilterGroup } from '~/modules/opportunities/automated-email/settings/types';
import type { TStoredFilters, TStoredRange } from '~/misc/listings/types';
import {
  EContactType,
  EDateFilterOptions,
  EHiddenFilter,
  EMarketingConsent,
  EPropertyCategory,
} from '~/misc/listings/types';
import { EAutomatedEmailFilterGroupId } from '~/modules/opportunities/automated-email/settings/types';

// Config
import {
  HIDDEN_FILTER_OPTIONS,
  LISTINGS_BEDS_OPTIONS,
  LISTINGS_DATE_OPTIONS,
  LISTINGS_TYPE_OPTIONS,
} from '~/misc/listings/config';

// Utils
import { formatPrice } from '~/misc/utils/number';
import { DateTimeFormatter } from '~/misc/utils/datetime';

const CONTACT_TYPE_LABELS: Record<EContactType, string> = {
  [EContactType.VENDOR]: 'Vendors',
  [EContactType.LANDLORD]: 'Landlords',
  [EContactType.APPLICANT]: 'Applicants',
};

const MARKETING_CONSENT_LABELS: Record<EMarketingConsent, string> = {
  [EMarketingConsent.GRANT]: 'Yes',
  [EMarketingConsent.DENY]: 'Deny',
  [EMarketingConsent.NOT_ASKED]: 'Not set',
};

const isRangeEmpty = (range: TStoredRange | null | undefined): boolean =>
  !range || (range.from == null && range.to == null);

const formatRangeBadge = (
  range: TStoredRange | null | undefined,
  format: (value: number) => string
): string | undefined => {
  if (isRangeEmpty(range)) {
    return undefined;
  }

  const from: number | null | undefined = range?.from;
  const to: number | null | undefined = range?.to;

  if (from != null && to != null) {
    return `${format(from)} – ${format(to)}`;
  }

  if (from != null) {
    return `${format(from)}+`;
  }

  return `Up to ${format(to as number)}`;
};

const formatBedValue = (value: number): string => {
  const option = LISTINGS_BEDS_OPTIONS.find((o) => o.value === value);

  if (option) {
    return option.label;
  }

  return `${value} ${value === 1 ? 'bed' : 'beds'}`;
};

const buildPropertyBadges = (filters: Partial<TStoredFilters>): string[] => {
  const badges: string[] = [];

  const priceBadge: string | undefined = formatRangeBadge(filters.price_range, (v) =>
    formatPrice(v)
  );

  if (priceBadge) {
    badges.push(priceBadge);
  }

  const bedsBadge: string | undefined = formatRangeBadge(filters.bed_range, formatBedValue);

  if (bedsBadge) {
    badges.push(bedsBadge);
  }

  if (filters.category) {
    const label: string | undefined = LISTINGS_TYPE_OPTIONS.find(
      (o) => o.value === (filters.category as EPropertyCategory)
    )?.label;

    if (label) {
      badges.push(label);
    }
  }

  if (filters.date) {
    const label: string | undefined = LISTINGS_DATE_OPTIONS.find(
      (o) => o.value === (filters.date as EDateFilterOptions)
    )?.label;

    if (label) {
      badges.push(label);
    }
  }

  if (filters.hidden && filters.hidden !== EHiddenFilter.DONT_SHOW) {
    const label: string | undefined = HIDDEN_FILTER_OPTIONS.find(
      (o) => o.value === filters.hidden
    )?.label;

    if (label) {
      badges.push(label);
    }
  }

  if (filters.exclude_crm_activity_since) {
    badges.push(`No CRM activity since ${DateTimeFormatter.toDayMonthYear(filters.exclude_crm_activity_since)}`);
  }

  if (filters.exclude_sold_since != null) {
    badges.push(filters.exclude_sold_since ? 'Exclude recently sold' : 'Include recently sold');
  }

  if (filters.exclude_archived != null) {
    badges.push(filters.exclude_archived ? 'Exclude archived' : 'Include archived');
  }

  if (filters.contactable_only) {
    badges.push('Contactable only');
  }

  return badges;
};

const buildContactTypeBadge = (contactTypes: EContactType[] | undefined): string | undefined => {
  if (!contactTypes || contactTypes.length === 0 || contactTypes.length === 3) {
    return undefined;
  }

  return contactTypes.map((type: EContactType) => CONTACT_TYPE_LABELS[type]).join(' + ');
};

const buildEntityStatusBadges = (
  entityStatus: number[] | undefined,
  entityStatusNames?: Record<number, string>
): string[] => {
  if (!entityStatus || entityStatus.length === 0) {
    return [];
  }

  const names: (string | undefined)[] = entityStatus.map((id: number) => entityStatusNames?.[id]);

  if (names.every((name: string | undefined): name is string => Boolean(name))) {
    return names as string[];
  }

  return [`${entityStatus.length} ${entityStatus.length === 1 ? 'status' : 'statuses'}`];
};

const buildDataBadges = (
  filters: Partial<TStoredFilters>,
  entityStatusNames?: Record<number, string>
): string[] => {
  const badges: string[] = [];

  const contactTypeBadge: string | undefined = buildContactTypeBadge(filters.contact_type);

  if (contactTypeBadge) {
    badges.push(contactTypeBadge);
  }

  if (filters.vendor_since) {
    badges.push(`Vendors since ${DateTimeFormatter.toDayMonthYear(filters.vendor_since)}`);
  }

  if (filters.landlord_since) {
    badges.push(`Landlords since ${DateTimeFormatter.toDayMonthYear(filters.landlord_since)}`);
  }

  if (filters.applicant_since) {
    badges.push(`Applicants since ${DateTimeFormatter.toDayMonthYear(filters.applicant_since)}`);
  }

  badges.push(...buildEntityStatusBadges(filters.entity_status, entityStatusNames));

  if (filters.exclude_actioned_properties) {
    badges.push('Unactioned listings only');
  }

  return badges;
};

const buildConsentBadges = (marketingConsent: EMarketingConsent[] | undefined): string[] => {
  if (!marketingConsent || marketingConsent.length === 0) {
    return [];
  }

  return marketingConsent.map((consent: EMarketingConsent) => MARKETING_CONSENT_LABELS[consent]);
};

/**
 * Maps the patch's stored automated-email filters into the three display groups
 * (Property / Data / Consent) with human-friendly badge labels. Groups without
 * any meaningful badges are omitted.
 */
export const formatPatchFilterGroups = (
  filters: Partial<TStoredFilters>,
  entityStatusNames?: Record<number, string>
): TAutomatedEmailFilterGroup[] => {
  const groups: TAutomatedEmailFilterGroup[] = [
    {
      id: EAutomatedEmailFilterGroupId.PROPERTY,
      label: 'Property',
      badges: buildPropertyBadges(filters),
    },
    {
      id: EAutomatedEmailFilterGroupId.DATA,
      label: 'Data',
      badges: buildDataBadges(filters, entityStatusNames),
    },
    {
      id: EAutomatedEmailFilterGroupId.CONSENT,
      label: 'Consent',
      badges: buildConsentBadges(filters.marketing_consent),
    },
  ];

  return groups.filter((group: TAutomatedEmailFilterGroup) => group.badges.length > 0);
};
